import { z } from 'zod'

import {
  recordSchema,
  recordDetailSchema,
  recordListPageSchema,
  recordSummarySchema,
  createRecordRequestSchema,
  createRecordResponseSchema,
  updateRecordValueRequestSchema,
  updateRecordValueResponseSchema,
  updateRecordRequestSchema,
  trashedRecordItemSchema,
  trashedRecordPageSchema,
  type Record,
  type RecordDetail,
  buttonClickResponseSchema,
  type ButtonClickResponse,
  type RecordListPage,
  type RecordSearchRequest,
  type RecordSummary,
  type CreateRecordRequest,
  type CreateRecordResponse,
  type UpdateRecordValueRequest,
  type UpdateRecordValueResponse,
  type UpdateRecordRequest,
  type TrashedRecordItem,
  type TrashedRecordPage,
} from '@/types/api/record'
import { trashStatsSchema, type TrashStats } from '@/types/api/trash'

import { apiClient } from './client'


/** One page of a group's records with stacked filters (permission-filtered on the server). */
export async function searchRecords(recordGroupId: string, request: RecordSearchRequest): Promise<RecordListPage> {
  const response = await apiClient.post(`/records/record-group/${recordGroupId}/search`, request)
  return recordListPageSchema.parse(response.data)
}

/** Downloads the records matching the request as CSV (needs record:export). */
export async function exportRecordsCsv(recordGroupId: string, request: RecordSearchRequest): Promise<Blob> {
  const response = await apiClient.post(`/records/record-group/${recordGroupId}/export`, request, { responseType: 'blob' })
  return response.data as Blob
}

export async function getRecordSummary(recordGroupId: string, includeSubgroups = false): Promise<RecordSummary> {
  const response = await apiClient.get(`/records/record-group/${recordGroupId}/summary`, {
    params: { includeSubgroups },
  })
  return recordSummarySchema.parse(response.data)
}

export async function getRecord(recordId: string, options?: { suppressErrorToast?: boolean }): Promise<RecordDetail> {
  const response = await apiClient.get(`/records/${recordId}`, options)
  return recordDetailSchema.parse(response.data)
}

/** Clicks a button field of a record; the caller reports every error itself (409 = a one-shot button already used). */
export async function clickRecordButton(recordId: string, fieldKey: string): Promise<ButtonClickResponse> {
  const response = await apiClient.post(`/records/${recordId}/buttons/${encodeURIComponent(fieldKey)}/click`, undefined, {
    suppressErrorToast: true,
  })
  return buttonClickResponseSchema.parse(response.data)
}

export async function createRecord(data: CreateRecordRequest): Promise<CreateRecordResponse> {
  const validated = createRecordRequestSchema.parse(data)
  const response = await apiClient.post('/records', validated)
  return createRecordResponseSchema.parse(response.data)
}

export async function deleteRecord(recordId: string): Promise<void> {
  await apiClient.delete(`/records/${recordId}`)
}

export async function updateRecord(recordId: string, data: UpdateRecordRequest): Promise<Record> {
  const validated = updateRecordRequestSchema.parse(data)
  const response = await apiClient.put(`/records/${recordId}`, validated)
  return recordSchema.parse(response.data)
}

export async function updateRecordValue(
  recordGroupId: string,
  data: UpdateRecordValueRequest
): Promise<UpdateRecordValueResponse> {
  const validated = updateRecordValueRequestSchema.parse(data)
  const response = await apiClient.put(`/record-values/record-group/${recordGroupId}`, validated)
  return updateRecordValueResponseSchema.parse(response.data)
}

export async function getTrashedRecords(pageNumber: number, pageSize: number): Promise<TrashedRecordPage> {
  const response = await apiClient.get(`/records/trash`, {
    params: { page: pageNumber, size: pageSize },
  })
  return trashedRecordPageSchema.parse(response.data)
}

export async function getAllTrashedRecords(): Promise<TrashedRecordItem[]> {
  const response = await apiClient.get('/records/trash/all')
  return z.array(trashedRecordItemSchema).parse(response.data)
}

export async function searchTrashedRecords(query: string, pageNumber: number, pageSize: number): Promise<TrashedRecordPage> {
  const response = await apiClient.get(`/records/trash/search`, {
    params: { query, page: pageNumber, size: pageSize },
  })
  return trashedRecordPageSchema.parse(response.data)
}

export async function getRecordTrashStats(): Promise<TrashStats> {
  const response = await apiClient.get('/records/trash/stats')
  return trashStatsSchema.parse(response.data)
}

export async function restoreRecord(recordId: string): Promise<TrashedRecordItem> {
  const response = await apiClient.post(`/records/trash/${recordId}/restore`, undefined, {
    suppressErrorToast: true,
  })
  return trashedRecordItemSchema.parse(response.data)
}

export async function permanentlyDeleteRecord(recordId: string): Promise<void> {
  await apiClient.delete(`/records/trash/${recordId}`, {
    suppressErrorToast: true,
  })
}
